import React, { useState, useEffect } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import ToggleSwitch from "aldoo-ra/ToggleSwitch"
import Button from "aldoo-ra/Button"
import { showNotification } from "aldoo-ra/Notification"
import { api } from "aldoo-ra/API"

export default function ContentSettingsDialog({
  isOpen,
  onClose,
  contentData,
  contentType,
  onSave,
  // the reader's own setting, kept in this browser per content type (saved as it is switched)
  showChangesOnSave = true,
  onShowChangesOnSave,
}) {
  const [aldooCore, setAldooCore] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (contentData) {
      setAldooCore(contentData.aldooCore === true)
    }
  }, [contentData, isOpen])

  // Get content name from various possible locations
  const getContentName = () => {
    if (contentData?.content?.name) return contentData.content.name
    if (contentData?.content?.type) return contentData.content.type
    if (contentData?.name) return contentData.name
    return "Unnamed Content"
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const crudAPI = api("crud")

      // Determine the model type
      // Content types are stored in CMSType, everything else in CMSContent
      let modelType = "CMSContent"
      if (contentType === "ContentType" || contentType === "CMSType") {
        modelType = "CMSType"
      }
      // Note: contentType is the TYPE of content (e.g. "SystemTestType"),
      // but the MongoDB model is always "CMSContent" for content items

      console.log("=== ContentSettingsDialog SAVE ===")
      console.log("contentData:", JSON.stringify(contentData, null, 2))
      console.log("modelType:", modelType)
      console.log("aldooCore value:", aldooCore)
      console.log("Update payload:", { model: modelType, match: { _id: contentData._id }, data: { aldooCore } })

      // Update the aldooCore field directly in the database
      const result = await crudAPI({
        update: {
          model: modelType,
          match: { _id: contentData._id },
          data: { aldooCore }
        }
      })

      console.log("CRUD API result:", result)

      // Check if update was successful
      if (result.error) {
        throw new Error(result.error)
      }

      showNotification({
        message: "Settings saved successfully",
        className: "bg-admin_success text-white",
      })

      // Call onSave callback to trigger a refresh of the content from the database
      // This ensures the parent component gets the updated metadata
      if (onSave) {
        await onSave({ aldooCore })
      }

      onClose()
    } catch (error) {
      console.error("Error saving settings:", error)
      showNotification({
        message: `Failed to save settings: ${error.message}`,
        className: "bg-admin_error text-white",
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      variant="admin_centered"
      title="Content Settings"
    >
      <div className="p-6 space-y-6 min-w-[500px]">
        {/* Content Info */}
        <div className="pb-4 border-b border-admin_bordered">
          <Typography variant="admin_txt1" className="text-admin_text font-medium mb-1">
            {getContentName()}
          </Typography>
          {contentType && (
            <Typography variant="admin_txt2" className="text-admin_text_secondary">
              Type: {contentType}
            </Typography>
          )}
        </div>

        {/* Aldoo Core Toggle */}
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-6">
            <Typography variant="admin_txt1" className="text-admin_text font-medium">
              Aldoo Core Content
            </Typography>
            <ToggleSwitch
              isOn={aldooCore}
              onChange={setAldooCore}
            />
          </div>

          {/* Description */}
          <Typography variant="admin_txt2" className="text-admin_text_secondary max-w-[350px]">
            Mark this content as core Aldoo content. Core content is exported with update content packs
            and used when setting up new installs to provide baseline content.
          </Typography>
        </div>

        {/* Show Changes On Save: yours, for every item of this type */}
        {onShowChangesOnSave && (
          <div className="space-y-3 pt-4 border-t border-admin_bordered">
            <div className="flex items-center justify-between gap-6">
              <Typography variant="admin_txt1" className="text-admin_text font-medium">
                Show Changes On Save
              </Typography>
              <ToggleSwitch isOn={showChangesOnSave} onChange={(on) => onShowChangesOnSave(!!on)} />
            </div>
            <Typography variant="admin_txt2" className="text-admin_text_secondary max-w-[350px]">
              Before saving, list what changed (each change can be taken back) and ask. Your own setting in this
              browser, for every {contentType || "content"} item; it applies at once.
            </Typography>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-6 border-t border-admin_bordered flex items-center justify-end">
        <Button
          variant="admin_primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </Modal>
  )
}
